/**
 * Utilidades para el manejo de tareas
 */

/**
 * Genera un timestamp en el formato local "HH:MM:SS DD-MM-YYYY" (internamente incluye segundos)
 * @param {Date} date - Fecha opcional, si no se proporciona usa la fecha actual
 * @returns {string} - Timestamp formateado como "HH:MM:SS DD-MM-YYYY"
 */
export const generateLocalTimestamp = (date = null) => {
  const now = date || new Date();
  
  try {
    // Formatear la hora con segundos
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    const seconds = now.getSeconds().toString().padStart(2, '0');
    
    // Formatear la fecha
    const day = now.getDate().toString().padStart(2, '0');
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const year = now.getFullYear();
    
    // Usar el formato con segundos para lógica interna (con 1 espacio entre hora y fecha)
    return `${hours}:${minutes}:${seconds} ${day}-${month}-${year}`;
  } catch (error) {
    console.error('Error al generar timestamp local:', error);
    // Fallback al formato ISO si hay error
    return now.toISOString();
  }
};

/**
 * Verifica si una tarea debe ser archivada automáticamente
 * Una tarea debe archivarse si:
 * - Está completada (status === 'done')
 * - Han pasado exactamente 72 horas desde su fecha de vencimiento
 * - No está ya archivada
 * 
 * @param {Object} task - La tarea a verificar
 * @returns {boolean} - true si la tarea debe ser archivada
 */
export const shouldTaskBeArchived = (task) => {
  // Verificar que la tarea esté completada
  if (task.status !== 'done') {
    return false;
  }

  // Verificar que no esté ya archivada
  if (task.ArchivedDate === true) {
    return false;
  }

  // Verificar que tenga fecha de finalización
  if (!task.completedDate) {
    return false;
  }

  try {
    // Parsear la fecha de finalización usando una función robusta
    const completedDate = parseCompletedDate(task.completedDate);
    if (!completedDate) {
      return false;
    }

    // Calcular la diferencia en milisegundos
    const now = new Date();
    const timeDifference = now.getTime() - completedDate.getTime();
    
    // 72 horas en milisegundos = 72 * 60 * 60 * 1000
    const seventyTwoHours = 72 * 60 * 60 * 1000;
    
    // Verificar si han pasado exactamente 72 horas o más desde la finalización
    return timeDifference >= seventyTwoHours;
  } catch (error) {
    console.error('Error al verificar si la tarea debe ser archivada:', error);
    return false;
  }
};

/**
 * Filtra las tareas para excluir las archivadas
 * @param {Array} tasks - Array de tareas
 * @returns {Array} - Array de tareas sin las archivadas
 */
export const filterArchivedTasks = (tasks) => {
  if (!Array.isArray(tasks)) {
    return [];
  }
  
  return tasks.filter(task => task.ArchivedDate !== true);
};

/**
 * Procesa las tareas para archivar automáticamente las que cumplan los criterios
 * @param {Array} tasks - Array de tareas
 * @returns {Array} - Array de tareas con las actualizaciones de archivado
 */
export const processTasksForAutoArchiving = (tasks) => {
  if (!Array.isArray(tasks)) {
    return [];
  }

  return tasks.map(task => {
    if (shouldTaskBeArchived(task)) {
      return {
        ...task,
        ArchivedDate: true
      };
    }
    return task;
  });
};

/**
 * Obtiene estadísticas de archivado para un conjunto de tareas
 * @param {Array} tasks - Array de tareas
 * @returns {Object} - Objeto con estadísticas de archivado
 */
export const getArchivingStats = (tasks) => {
  if (!Array.isArray(tasks)) {
    return {
      total: 0,
      completed: 0,
      archived: 0,
      pendingArchival: 0,
      tasksToArchive: []
    };
  }

  const completed = tasks.filter(task => task.status === 'done');
  const archived = tasks.filter(task => task.ArchivedDate === true);
  const tasksToArchive = tasks.filter(task => shouldTaskBeArchived(task));

  return {
    total: tasks.length,
    completed: completed.length,
    archived: archived.length,
    pendingArchival: tasksToArchive.length,
    tasksToArchive: tasksToArchive
  };
};

/**
 * Calcula el tiempo restante hasta que una tarea completada sea archivada
 * @param {Object} task - La tarea a verificar
 * @returns {Object|null} - Objeto con información del tiempo restante o null si no aplica
 */
/**
 * Función auxiliar para parsear fechas de vencimiento en diferentes formatos
 * @param {string} dateString - Fecha en formato DD-MM-YYYY, YYYY-MM-DD, o ISO
 * @returns {Date|null} - Objeto Date parseado o null si es inválido
 */
// eslint-disable-next-line no-unused-vars
const parseEndDate = (dateString) => {
  if (!dateString) return null;
  
  try {
    // Formato DD-MM-YYYY (formato común en la aplicación)
    if (typeof dateString === 'string' && dateString.match(/^\d{2}-\d{2}-\d{4}$/)) {
      const [day, month, year] = dateString.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day, 23, 59, 59); // Final del día
    }
    
    // Formato YYYY-MM-DD
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day, 23, 59, 59); // Final del día
    }
    
    // Intentar parsing directo para otros formatos (ISO, etc.)
    const fallbackDate = new Date(dateString);
    return isNaN(fallbackDate.getTime()) ? null : fallbackDate;
  } catch (error) {
    console.error('Error al parsear fecha de vencimiento:', error);
    return null;
  }
};

/**
 * Función auxiliar para parsear fechas de finalización en diferentes formatos
 * @param {string} completedDate - Fecha en formato "HH:MM:SS DD-MM-YYYY" o "HH:MM DD-MM-YYYY"
 * @returns {Date|null} - Objeto Date parseado o null si es inválido
 */
const parseCompletedDate = (completedDate) => {
  if (!completedDate) return null;

  try {
    // Formato "HH:MM:SS DD-MM-YYYY" (con segundos)
    const timestampWithSecondsFormat = /^(\d{2}):(\d{2}):(\d{2}) (\d{2})-(\d{2})-(\d{4})$/;
    const match = completedDate.match(timestampWithSecondsFormat);
    if (match) {
      const [, hours, minutes, seconds, day, month, year] = match;
      return new Date(
        parseInt(year), 
        parseInt(month) - 1, 
        parseInt(day), 
        parseInt(hours), 
        parseInt(minutes), 
        parseInt(seconds)
      );
    }

    // Formato "HH:MM DD-MM-YYYY" (sin segundos, legacy)
    const timestampFormat = /^(\d{2}):(\d{2}) (\d{2})-(\d{2})-(\d{4})$/;
    const match2 = completedDate.match(timestampFormat);
    if (match2) {
      const [, hours, minutes, day, month, year] = match2;
      return new Date(
        parseInt(year), 
        parseInt(month) - 1, 
        parseInt(day), 
        parseInt(hours), 
        parseInt(minutes), 
        0
      );
    }

    // Formato ISO o cualquier otro que Date pueda parsear
    const parsed = new Date(completedDate);
    if (!isNaN(parsed.getTime())) {
      return parsed;
    }

    return null;
  } catch (error) {
    console.error('Error parsing completed date:', completedDate, error);
    return null;
  }
};

export const getTimeUntilArchival = (task) => {
  // Verificar que la tarea esté completada
  if (task.status !== 'done') {
    return null;
  }

  // Verificar que no esté ya archivada
  if (task.ArchivedDate === true) {
    return null;
  }

  // Verificar que tenga fecha de finalización
  if (!task.completedDate) {
    return null;
  }

  try {
    // Parsear la fecha de finalización usando la función auxiliar
    const completedDate = parseCompletedDate(task.completedDate);
    if (!completedDate) {
      console.warn('No se pudo parsear la fecha de finalización:', task.completedDate);
      return null;
    }

    // Calcular cuándo será archivada (72 horas después de la fecha de finalización)
    const archivalDate = new Date(completedDate.getTime() + (72 * 60 * 60 * 1000));
    const now = new Date();
    const timeUntilArchival = archivalDate.getTime() - now.getTime();

    if (timeUntilArchival <= 0) {
      return {
        shouldBeArchived: true,
        timeRemaining: 0,
        archivalDate: archivalDate
      };
    }

    // Convertir milisegundos a horas y minutos
    const hoursRemaining = Math.floor(timeUntilArchival / (60 * 60 * 1000));
    const minutesRemaining = Math.floor((timeUntilArchival % (60 * 60 * 1000)) / (60 * 1000));

    return {
      shouldBeArchived: false,
      timeRemaining: timeUntilArchival,
      hoursRemaining: hoursRemaining,
      minutesRemaining: minutesRemaining,
      archivalDate: archivalDate
    };
  } catch (error) {
    console.error('Error al calcular tiempo hasta archivado:', error);
    return null;
  }
};

/**
 * Formatea el tiempo restante hasta el archivado en un string legible
 * @param {Object} timeInfo - Información del tiempo obtenida de getTimeUntilArchival
 * @returns {string} - String formateado con el tiempo restante
 */
export const formatTimeUntilArchival = (timeInfo) => {
  if (!timeInfo) {
    return 'No disponible';
  }

  if (timeInfo.shouldBeArchived) {
    return 'Listo para archivar';
  }

  const { hoursRemaining, minutesRemaining } = timeInfo;
  
  if (hoursRemaining > 0) {
    return `${hoursRemaining}h ${minutesRemaining}m restantes`;
  } else {
    return `${minutesRemaining}m restantes`;
  }
};

/**
 * Calcula los días transcurridos desde una fecha hasta ahora
 * @param {string|Date} dateString - Fecha de inicio (timestamp, fecha ISO, o formato local)
 * @returns {number} - Número de días transcurridos
 */
export const calculateDaysElapsed = (dateString) => {
  if (!dateString) {
    return 0;
  }

  try {
    let startDate;

    // Si es un string con formato local "HH:MM DD-MM-YYYY"
    if (typeof dateString === 'string' && dateString.includes(' ') && dateString.includes('-')) {
      const parts = dateString.split(' ');
      if (parts.length === 2) {
        const datePart = parts[1]; // DD-MM-YYYY
        const [day, month, year] = datePart.split('-');
        if (day && month && year) {
          // Crear fecha a las 00:00:00 del día especificado
          startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0);
        }
      }
    }

    // Si no se pudo parsear con el formato local, intentar con Date constructor
    if (!startDate || isNaN(startDate.getTime())) {
      startDate = new Date(dateString);
    }

    // Verificar que la fecha sea válida
    if (isNaN(startDate.getTime())) {
      return 0;
    }

    // Usar fecha actual también a las 00:00:00 para una comparación más consistente
    const now = new Date();
    const currentDateMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    
    const timeDifference = currentDateMidnight.getTime() - startDate.getTime();
    const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

    return Math.max(0, daysDifference);
  } catch (error) {
    console.error('Error al calcular días transcurridos:', error);
    return 0;
  }
};

/**
 * Formatea los días transcurridos en un string legible
 * @param {number} days - Número de días
 * @returns {string} - String formateado
 */
export const formatDaysElapsed = (days) => {
  if (days === 0) {
    return 'Hoy';
  } else if (days === 1) {
    return '1 día';
  } else {
    return `${days} días`;
  }
};

/**
 * Calcula los días transcurridos desde la creación de una tarea
 * @param {Object} task - La tarea
 * @returns {number} - Número de días desde la creación
 */
export const getTaskAge = (task) => {
  if (!task) {
    return 0;
  }

  // Intentar usar createdTimestamp primero, luego otros campos de fecha
  const creationDate = task.createdTimestamp || task.createdAt || task.timestamp;
  
  if (!creationDate) {
    return 0;
  }

  return calculateDaysElapsed(creationDate);
};

/**
 * Calcula los días transcurridos desde un registro de actividad
 * @param {Object} activityEntry - Entrada del historial de actividad
 * @returns {number} - Número de días desde el registro
 */
export const getActivityAge = (activityEntry) => {
  if (!activityEntry || !activityEntry.timestamp) {
    return 0;
  }

  return calculateDaysElapsed(activityEntry.timestamp);
}; 